// Inventario.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import './Inventario.css';
import ensureAbsoluteImg from '../utils/urls';
import { getProductColor, setProductColor } from '../utils/colorStore';

function Inventario({ usuario }) {
  const [inventario, setInventario] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [tipoActivo, setTipoActivo] = useState('ALL');
  const [ordenSerie, setOrdenSerie] = useState(null); // 'asc' | 'desc' | null
  const tiposDropdownRef = useRef(null);

  // Fetch productos desde backend
  useEffect(() => {
    const fetchProductos = async () => {
      try {
        setCargando(true);
        setError(null);
        const resp = await fetch('/api/productos');
        if (!resp.ok) throw new Error('Error al obtener productos');
        const data = await resp.json();
        // Mapear campos del backend a los usados en el componente
        // Backend: id_producto, concepto, codigo, precio, cantidad, color, talla, descripcion
        const inferirTipo = (nombre='') => {
          const clean = String(nombre).trim().toUpperCase();
          if(!clean) return 'OTROS';
          // Tipo = primera palabra antes de espacio o ' DE '
          const hastaDe = clean.split(' DE ')[0];
          const primera = hastaDe.split(/\s+/)[0];
          return primera || 'OTROS';
        };
        const mapeados = data.map(p => ({
          id: p.id_producto,
          nombre: p.concepto || 'Sin nombre',
          serie: p.codigo || '',
          precio: p.precio ?? 0,
          stock: p.cantidad ?? 0,
          stockMinimo: 5,
          imagen: (() => {
            const candidato = p.imagenes && p.imagenes.length > 0
              ? (p.imagenes.find(img => img.es_principal)?.imagen_url || p.imagenes[0].imagen_url)
              : null;
            return ensureAbsoluteImg(candidato);
          })(),
          imagenes: p.imagenes || [],
          cantidadEtiquetas: 1,
            seleccionado: false,
          codigo: p.codigo || (Date.now().toString() + Math.floor(Math.random()*1000)),
          talla: p.talla || '',
          tipo: inferirTipo(p.concepto)
        }));
        setInventario(mapeados);
      } catch (e) {
        setError(e.message);
      } finally {
        setCargando(false);
      }
    };
    fetchProductos();
  }, []);

  const [productoEditando, setProductoEditando] = useState(null);
  const [nuevoStock, setNuevoStock] = useState('');
  const [nuevoPrecio, setNuevoPrecio] = useState('');
  const [nuevoProducto, setNuevoProducto] = useState({
    nombre: '', serie: '', precio: '', stock: '', stockMinimo: 5, imagen: '', color: ''
  });
  const [mostrandoAgregar, setMostrandoAgregar] = useState(false);
  const [mostrandoImpresion, setMostrandoImpresion] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [busquedaEtiquetas, setBusquedaEtiquetas] = useState('');
  const [tamañoEtiqueta, setTamañoEtiqueta] = useState('mediano'); // minis, pequeño, mediano, grande
  const [dispositivoImpresion, setDispositivoImpresion] = useState('normal'); // 'normal' | 'niimbot'

  // Estados para gestión de imágenes
  const [modalImagenes, setModalImagenes] = useState({ open: false, producto: null });
  const [imagenesProducto, setImagenesProducto] = useState([]);
  const [subiendoImagenes, setSubiendoImagenes] = useState(false);

  // Estados para gestión de variantes
  const [modalVariantes, setModalVariantes] = useState({ open: false, producto: null });
  const [variantes, setVariantes] = useState([]);
  const [nuevaVariante, setNuevaVariante] = useState({ color: '#FF0000', stock: 0 });
  const [editandoVariante, setEditandoVariante] = useState(null);

  // NUEVO: estado para modal de alertas
  const [mostrarModalAlertas, setMostrarModalAlertas] = useState(false);
  const [tipoAlerta, setTipoAlerta] = useState(null); // 'bajo' | 'sin'
  
  // NUEVO: estado para el desplegable de tipos
  const [mostrarTipos, setMostrarTipos] = useState(false);

  const actualizarStock = async (id, cantidad) => {
    const valor = parseInt(cantidad) || 0;
    try {
      // Optimistic update
      setInventario(prev => prev.map(item => item.id === id ? { ...item, stock: valor } : item));
      const resp = await fetch(`/api/productos/${id}/stock`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cantidad: valor })
      });
      if (!resp.ok) throw new Error('Error al actualizar stock');
      const actualizado = await resp.json();
      setInventario(prev => prev.map(it => it.id === id ? { ...it, stock: actualizado.cantidad ?? valor } : it));
    } catch (e) {
      alert(e.message);
      // Revert could be implemented by refetch; quick refetch approach:
      try {
        const ref = await fetch('/api/productos');
        if (ref.ok) {
          const data = await ref.json();
          const inferirTipo = (nombre='') => {
            const clean = String(nombre).trim().toUpperCase();
            if(!clean) return 'OTROS';
            const hastaDe = clean.split(' DE ')[0];
            const primera = hastaDe.split(/\s+/)[0];
            return primera || 'OTROS';
          };
          const mapeados = data.map(p => ({
            id: p.id_producto,
            nombre: p.concepto || 'Sin nombre',
            serie: p.codigo || '',
            precio: p.precio ?? 0,
            stock: p.cantidad ?? 0,
            stockMinimo: 5,
            imagen: 'https://via.placeholder.com/150x150?text=Producto',
            cantidadEtiquetas: 1,
            seleccionado: false,
            codigo: p.codigo || (Date.now().toString() + Math.floor(Math.random()*1000)),
            talla: p.talla || '',
            tipo: inferirTipo(p.concepto)
          }));
          setInventario(mapeados);
        }
      } catch {}
    } finally {
      setProductoEditando(null);
      setNuevoStock('');
    }
  };

  const iniciarEdicion = (producto) => {
    setProductoEditando(producto.id);
    setNuevoStock(producto.stock.toString());
    setNuevoPrecio(String(producto.precio));
  };

  const cancelarEdicion = () => {
    setProductoEditando(null);
    setNuevoStock('');
    setNuevoPrecio('');
  };

  const actualizarPrecio = async (id, precio) => {
    const val = parseFloat(precio);
    if (isNaN(val)) { alert('Precio inválido'); return; }
    try {
      // Optimistic update
      setInventario(prev => prev.map(item => item.id === id ? { ...item, precio: val } : item));
      const resp = await fetch(`/api/productos/${id}/precio`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ precio: val })
      });
      if (!resp.ok) throw new Error('Error al actualizar precio');
      const actualizado = await resp.json();
      setInventario(prev => prev.map(it => it.id === id ? { ...it, precio: actualizado.precio ?? val } : it));
    } catch (e) {
      alert(e.message);
    } finally {
      setProductoEditando(null);
      setNuevoPrecio('');
      setNuevoStock('');
    }
  };

  const manejarCambioFormulario = (campo, valor) => {
    setNuevoProducto(prev => ({ ...prev, [campo]: valor }));
  };

  const eliminarProducto = async (id, nombre) => {
    if (!window.confirm(`¿Estás seguro de eliminar el producto "${nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      const resp = await fetch(`/api/productos/${id}`, {
        method: 'DELETE'
      });
      if (!resp.ok) throw new Error('Error al eliminar producto');
      setInventario(prev => prev.filter(p => p.id !== id));
      alert('Producto eliminado correctamente');
    } catch (e) {
      alert('Error al eliminar: ' + e.message);
    }
  };

  // Funciones para gestión de imágenes
  const abrirModalImagenes = async (producto) => {
    setModalImagenes({ open: true, producto });
    try {
      const resp = await fetch(`/api/productos/${producto.id}/imagenes`);
      if (resp.ok) {
        const imagenes = await resp.json();
        setImagenesProducto(imagenes);
      }
    } catch (e) {
      console.error('Error cargando imágenes:', e);
    }
  };

  const cerrarModalImagenes = () => {
    setModalImagenes({ open: false, producto: null });
    setImagenesProducto([]);
  };

  const subirImagenes = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('imagenes', files[i]);
    }
    
    setSubiendoImagenes(true);
    try {
      const resp = await fetch(`/api/productos/${modalImagenes.producto.id}/imagenes`, {
        method: 'POST',
        body: formData
      });
      
      if (!resp.ok) throw new Error('Error al subir imágenes');
      
      const data = await resp.json();
      setImagenesProducto(prev => [...prev, ...data.imagenes]);
      
      // Actualizar inventario con la nueva imagen principal si es la primera
      if (imagenesProducto.length === 0 && data.imagenes.length > 0) {
        setInventario(prev => prev.map(p => 
          p.id === modalImagenes.producto.id 
            ? { ...p, imagen: ensureAbsoluteImg(data.imagenes[0].imagen_url) }
            : p
        ));
      }
      
      alert('Imágenes subidas correctamente');
      e.target.value = ''; // Resetear input
    } catch (e) {
      alert('Error al subir imágenes: ' + e.message);
    } finally {
      setSubiendoImagenes(false);
    }
  };

  const establecerImagenPrincipal = async (idImagen) => {
    try {
      const resp = await fetch(`/api/productos/${modalImagenes.producto.id}/imagenes/${idImagen}/principal`, {
        method: 'PUT'
      });
      
      if (!resp.ok) throw new Error('Error al establecer imagen principal');
      
      setImagenesProducto(prev => prev.map(img => ({
        ...img,
        es_principal: img.id_imagen === idImagen
      })));
      
      // Actualizar imagen en inventario
      const imgPrincipal = imagenesProducto.find(img => img.id_imagen === idImagen);
      if (imgPrincipal) {
        setInventario(prev => prev.map(p => 
          p.id === modalImagenes.producto.id 
            ? { ...p, imagen: ensureAbsoluteImg(imgPrincipal.imagen_url) }
            : p
        ));
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const eliminarImagen = async (idImagen) => {
    if (!window.confirm('¿Eliminar esta imagen?')) return;
    
    try {
      const resp = await fetch(`/api/productos/${modalImagenes.producto.id}/imagenes/${idImagen}`, {
        method: 'DELETE'
      });
      
      if (!resp.ok) throw new Error('Error al eliminar imagen');
      
      setImagenesProducto(prev => prev.filter(img => img.id_imagen !== idImagen));
      
      // Si se eliminó la imagen principal, actualizar con la primera disponible
      const imgEliminada = imagenesProducto.find(img => img.id_imagen === idImagen);
      if (imgEliminada?.es_principal) {
        const nuevasImagenes = imagenesProducto.filter(img => img.id_imagen !== idImagen);
        if (nuevasImagenes.length > 0) {
          setInventario(prev => prev.map(p => 
            p.id === modalImagenes.producto.id 
              ? { ...p, imagen: ensureAbsoluteImg(nuevasImagenes[0].imagen_url) }
              : p
          ));
        }
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  // Funciones para gestión de variantes
  const abrirModalVariantes = async (producto) => {
    setModalVariantes({ open: true, producto });
    try {
      const resp = await fetch(`/api/productos/${producto.id}/variantes`);
      if (resp.ok) {
        const vars = await resp.json();
        setVariantes(vars);
      }
    } catch (e) {
      console.error('Error cargando variantes:', e);
    }
  };

  const cerrarModalVariantes = () => {
    setModalVariantes({ open: false, producto: null });
    setVariantes([]);
    setNuevaVariante({ sku: '', color: '', talla: '', stock: 0, precio: '', codigo_barras: '' });
    setEditandoVariante(null);
  };

  const crearVariante = async () => {
    if (!nuevaVariante.color) {
      alert('Selecciona un color');
      return;
    }
    try {
      const body = {
        color: nuevaVariante.color,
        stock: parseInt(nuevaVariante.stock) || 0,
        activo: true
      };
      const resp = await fetch(`/api/productos/${modalVariantes.producto.id}/variantes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!resp.ok) throw new Error('Error al crear variante');
      const creada = await resp.json();
      setVariantes(prev => [...prev, creada]);
      setNuevaVariante({ color: '#FF0000', stock: 0 });
      alert('Variante creada correctamente');
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const actualizarVariante = async (idVariante, cambios) => {
    try {
      const resp = await fetch(`/api/productos/${modalVariantes.producto.id}/variantes/${idVariante}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cambios)
      });
      if (!resp.ok) throw new Error('Error al actualizar variante');
      const actualizada = await resp.json();
      setVariantes(prev => prev.map(v => v.id_variante === idVariante ? actualizada : v));
      setEditandoVariante(null);
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const eliminarVariante = async (idVariante) => {
    if (!window.confirm('¿Eliminar esta variante?')) return;
    try {
      const resp = await fetch(`/api/productos/${modalVariantes.producto.id}/variantes/${idVariante}`, {
        method: 'DELETE'
      });
      if (!resp.ok) throw new Error('Error al eliminar variante');
      setVariantes(prev => prev.filter(v => v.id_variante !== idVariante));
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const agregarProducto = async () => {
    if (!nuevoProducto.nombre || !nuevoProducto.serie || !nuevoProducto.precio) {
      alert('Completa los campos obligatorios (Nombre, Serie, Precio)');
      return;
    }
    try {
      const body = {
        concepto: nuevoProducto.nombre,
        codigo: nuevoProducto.serie,
        precio: parseFloat(nuevoProducto.precio) || null,
        cantidad: parseInt(nuevoProducto.stock) || 0,
        unidad: null,
        talla: null,
        color: null,
        descripcion: null,
        id_categoria: null
      };
      const resp = await fetch('/api/productos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!resp.ok) throw new Error('Error al crear producto');
      const creado = await resp.json();
      const inferirTipo = (nombre='') => {
        const clean = String(nombre).trim().toUpperCase();
        if(!clean) return 'OTROS';
        const hastaDe = clean.split(' DE ')[0];
        const primera = hastaDe.split(/\s+/)[0];
        return primera || 'OTROS';
      };
      const nuevo = {
        id: creado.id_producto,
        nombre: creado.concepto,
        serie: creado.codigo,
        precio: creado.precio ?? 0,
        stock: creado.cantidad ?? 0,
        stockMinimo: 5,
        imagen: 'https://via.placeholder.com/150x150?text=Producto',
        cantidadEtiquetas: 1,
        seleccionado: false,
        codigo: creado.codigo,
        talla: creado.talla || '',
        tipo: inferirTipo(creado.concepto)
      };
      // Guardar color en storage POS-only si el usuario lo proporcionó
      if (nuevoProducto.color) {
        setProductColor(nuevo.id, nuevoProducto.color);
        nuevo.color = nuevoProducto.color;
      }
      setInventario(prev => [...prev, nuevo]);
      setNuevoProducto({ nombre: '', serie: '', precio: '', stock: '', stockMinimo: 5, imagen: '' });
    } catch (e) {
      alert(e.message);
    }
  };

  const manejarSeleccionEtiqueta = (id, seleccionado) => {
    setInventario(prev => prev.map(p => p.id === id ? { ...p, seleccionado } : p));
  };

  const manejarCantidadEtiqueta = (id, cantidad) => {
    setInventario(prev => prev.map(p => p.id === id ? { ...p, cantidadEtiquetas: parseInt(cantidad) || 1 } : p));
  };

  const exportarEtiquetasExcel = () => {
    const productosSeleccionados = inventario.filter(p => p.seleccionado);
    productosSeleccionados.forEach(p => {
      const data = [];
      for (let i = 0; i < p.cantidadEtiquetas; i++) {
        data.push({
          Nombre: p.nombre,
          Serie: p.serie,
          Precio: p.precio,
          Stock: p.stock,
          Código: p.codigo
        });
      }
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, p.nombre);
      XLSX.writeFile(wb, `${p.nombre}_etiquetas.xlsx`);
    });
  };

  // Función de escape HTML
  function escapeHtml(str) {
    if (!str && str !== 0) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // ---------- imprimirEtiquetas ajustada al formato OD5160 (Avery 5160) ----------
 const imprimirEtiquetas = () => {
  const productosSeleccionados = inventario.filter(p => p.seleccionado);
  if (productosSeleccionados.length === 0) return alert("Selecciona al menos un producto");

  const etiquetas = [];
  const etiquetasConfig = [];

  // Generar etiquetas seleccionadas
  productosSeleccionados.forEach(p => {
    for (let i = 0; i < p.cantidadEtiquetas; i++) {
      const id = `barcode-${p.codigo}-${i}-${Math.floor(Math.random() * 100000)}`;
      etiquetasConfig.push({ id, code: p.codigo });
      etiquetas.push({ id, nombre: p.nombre, precio: p.precio, serie: p.serie, talla: p.talla });
    }
  });

  let contenidoHtml = '';

  if (dispositivoImpresion === "normal") {
    // --- OD5160: 30 etiquetas por hoja ---
    while (etiquetas.length % 30 !== 0) etiquetas.push({ id: null, nombre: '', precio: '', serie: '' }); // rellenar hasta múltiplo de 30

    for (let i = 0; i < etiquetas.length; i += 30) {
      const bloque = etiquetas.slice(i, i + 30).map(e => `
        <div class="etiqueta">
          <div class="nombre">${escapeHtml(e.nombre + (e.talla ? ' ('+ e.talla +')' : ''))}</div>
          <div class="precio">$${Number(e.precio).toFixed(2)}</div>
          <svg id="${e.id || ''}"></svg>
          <div class="serie">${escapeHtml(e.serie)}</div>
        </div>
      `).join('');
      contenidoHtml += `<div class="pagina-etiquetas">${bloque}</div>`;
    }
  } else {
    // --- Niimbot B1: una etiqueta por página, horizontal ---
    contenidoHtml = etiquetas.map(e => `
      <div class="etiqueta">
        <div class="nombre">${escapeHtml(e.nombre + (e.talla ? ' ('+ e.talla +')' : ''))}</div>
        <div class="precio">$${Number(e.precio).toFixed(2)}</div>
        <svg id="${e.id}"></svg>
        <div class="serie">${escapeHtml(e.serie)}</div>
      </div>
    `).join('');
  }

  // Detectar si es dispositivo móvil
  const esMobil = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  if (esMobil) {
    // En móvil: crear vista previa en modal
    const modalPreview = document.createElement('div');
    modalPreview.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.9);z-index:9999;display:flex;flex-direction:column;';
    
    const toolbar = document.createElement('div');
    toolbar.style.cssText = 'background:#0d1846;padding:15px;display:flex;justify-content:space-between;align-items:center;';
    toolbar.innerHTML = `
      <button onclick="window.print()" style="background:#ff6a1a;color:#fff;border:none;padding:12px 24px;border-radius:6px;font-weight:600;font-size:16px;">🖨️ Imprimir</button>
      <button onclick="this.closest('[data-preview-modal]').remove()" style="background:#f44336;color:#fff;border:none;padding:12px 24px;border-radius:6px;font-weight:600;font-size:16px;">✕ Cerrar</button>
    `;
    
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'flex:1;width:100%;border:none;background:#fff;';
    iframe.srcdoc = `
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Etiquetas</title>
        <style>
          body { margin:0; padding:10px; font-family: Arial,sans-serif; background:#f5f5f5; }

          /* Vista previa en pantalla */
          @media screen {
            .pagina-etiquetas {
              background: #fff;
              margin: 10px auto;
              padding: 20px;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
              max-width: 800px;
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 10px;
            }
            .etiqueta {
              border: 1px solid #ddd;
              padding: 10px;
              text-align: center;
              background: #fff;
              border-radius: 4px;
            }
            .nombre { font-size:12px; font-weight:bold; margin-bottom:4px; }
            .precio { font-size:12px; margin-bottom:4px; color:#0d1846; }
            .serie { font-size:10px; color:#666; margin-top:4px; }
            svg { max-width: 100%; height: auto; }
          }

          /* OD5160 para impresión */
          @media print {
            body { background:#fff; padding:0; }
            .pagina-etiquetas {
              width: 8.5in;
              height: 11in;
              display: grid;
              grid-template-columns: repeat(3, 2.625in);
              grid-template-rows: repeat(10, 1in);
              padding-top: 0.5in;
              padding-left: 0.1875in;
              box-sizing: border-box;
              page-break-after: always;
              box-shadow: none;
              margin: 0;
            }
            .pagina-etiquetas .etiqueta {
              width: 2.625in;
              height: 1in;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              text-align: center;
              padding: 2px;
              box-sizing: border-box;
              border: none;
              border-radius: 0;
            }
            .pagina-etiquetas .nombre { font-size:9px; font-weight:bold; margin-bottom:2px; }
            .pagina-etiquetas .precio { font-size:9px; margin-bottom:2px; }
            .pagina-etiquetas svg { 
              width: 92%;
              max-width: 92%; 
              height: auto; 
              max-height: 15mm; 
              margin-left: -2mm;
            }
            .pagina-etiquetas .serie { font-size:7px; color:#333; }
          }

          /* Niimbot B1 */
          @media print {
            @page { size: 50mm 30mm landscape; margin:0; }
            .etiqueta {
              width:50mm; height:30mm;
              display:flex; flex-direction:column;
              justify-content:center; align-items:center;
              text-align:center; box-sizing:border-box;
              page-break-after: always;
              padding:2px; border:none;
            }
            .etiqueta .nombre { font-size:12px; font-weight:bold; margin-bottom:2px; }
            .etiqueta .precio { font-size:13px; font-weight:bold; margin-bottom:2px; }
            .etiqueta svg { 
              width: 92%;
              max-width: 92%; 
              height: auto; 
              max-height: 15mm; 
              margin-left: -2mm; 
            }
            .etiqueta .serie { font-size:8px; margin-top:2px; }
          }
        </style>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
      </head>
      <body>
        ${contenidoHtml}
        <script>
          const etiquetasData = ${JSON.stringify(etiquetasConfig)};
          etiquetasData.forEach(e => {
            const svg = document.getElementById(e.id);
            if(svg){
              JsBarcode(svg, e.code, { 
                format:"CODE128", 
                displayValue:true, 
                fontSize:8, 
                height:${dispositivoImpresion==="niimbot"?35:30}, 
                width:${dispositivoImpresion==="niimbot"?1.5:1}, 
                margin:0 
              });
            }
          });
        </script>
      </body>
      </html>
    `;
    
    modalPreview.setAttribute('data-preview-modal', 'true');
    modalPreview.appendChild(toolbar);
    modalPreview.appendChild(iframe);
    document.body.appendChild(modalPreview);
  } else {
    // En desktop: ventana popup tradicional
    const ventana = window.open('', '_blank', 'width=900,height=700');
    if (!ventana) {
      alert('Por favor permite las ventanas emergentes para ver la vista previa');
      return;
    }
    ventana.document.write(`
    <!doctype html>
    <html>
    <head>
      <meta charset="utf-8"/>
      <title>Etiquetas</title>
      <style>
        body { margin:0; padding:0; font-family: Arial,sans-serif; }

        /* --- OD5160 (hoja carta) --- */
        @media print {
          .pagina-etiquetas {
            width: 8.5in;
            height: 11in;
            display: grid;
            grid-template-columns: repeat(3, 2.625in);
            grid-template-rows: repeat(10, 1in);
            padding-top: 0.5in;
            padding-left: 0.1875in;
            box-sizing: border-box;
            page-break-after: always;
          }
          .pagina-etiquetas .etiqueta {
            width: 2.625in;
            height: 1in;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;
            padding: 2px;
            box-sizing: border-box;
          }
          .pagina-etiquetas .nombre { font-size:9px; font-weight:bold; margin-bottom:2px; }
          .pagina-etiquetas .precio { font-size:9px; margin-bottom:2px; }
          .pagina-etiquetas svg { 
            width: 92%;   /* un poco más pequeño para que no se recorten las barras */
            max-width: 92%; 
            height: auto; 
            max-height: 15mm; 
            margin-left: -2mm; /* corre todo 2mm a la izquierda */
          }
          .pagina-etiquetas .serie { font-size:7px; color:#333; }
        }

        /* --- Niimbot B1 --- */
        @media print {
          @page { size: 50mm 30mm landscape; margin:0; }
          .etiqueta {
            width:50mm; height:30mm;
            display:flex; flex-direction:column;
            justify-content:center; align-items:center;
            text-align:center; box-sizing:border-box;
            page-break-after: always; /* cada etiqueta en su propia página */
            padding:2px;
          }
          .etiqueta .nombre { font-size:12px; font-weight:bold; margin-bottom:2px; }
          .etiqueta .precio { font-size:13px; font-weight:bold; margin-bottom:2px; }
          .etiqueta svg { 
            width: 92%;   /* mismo ajuste aplicado */
            max-width: 92%; 
            height: auto; 
            max-height: 15mm; 
            margin-left: -2mm; 
          }
          .etiqueta .serie { font-size:8px; margin-top:2px; }
        }
      </style>
      <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
    </head>
    <body>
      ${contenidoHtml}
      <script>
        const etiquetasData = ${JSON.stringify(etiquetasConfig)};
        etiquetasData.forEach(e => {
          const svg = document.getElementById(e.id);
          if(svg){
            JsBarcode(svg, e.code, { 
              format:"CODE128", 
              displayValue:true, 
              fontSize:8, 
              height:${dispositivoImpresion==="niimbot"?35:30}, 
              width:${dispositivoImpresion==="niimbot"?1.5:1}, 
              margin:0 
            });
          }
        });
        setTimeout(()=>window.print(),300);
      </script>
    </body>
    </html>
  `);
    ventana.document.close();
  }
};


  // ---------- Búsqueda reutilizable (para modal de impresión y tabla principal) ----------
  const inventarioFiltradoBase = inventario.filter(p => {
    if (!busqueda) return true;
    const b = busqueda.toLowerCase();
    return (p.nombre || '').toLowerCase().includes(b) || (p.serie || '').toLowerCase().includes(b) || (p.talla || '').toLowerCase().includes(b);
  });

  const inventarioFiltrado = useMemo(() => {
    let arr = inventarioFiltradoBase;
    if (tipoActivo && tipoActivo !== 'ALL') {
      arr = arr.filter(p => (p.tipo || 'OTROS') === tipoActivo);
    }
    if (ordenSerie) {
      const toKey = (s='') => {
        const m = String(s).match(/\d+/);
        return m ? parseInt(m[0],10) : null;
      };
      arr = [...arr].sort((a,b) => {
        const ka = toKey(a.serie);
        const kb = toKey(b.serie);
        if(ka!=null && kb!=null){
          return ordenSerie === 'asc' ? (ka - kb) : (kb - ka);
        }
        // fallback lexicográfico
        const sa = String(a.serie||'');
        const sb = String(b.serie||'');
        const comp = sa.localeCompare(sb, undefined, { numeric:true, sensitivity:'base' });
        return ordenSerie === 'asc' ? comp : -comp;
      });
    }
    return arr;
  }, [inventarioFiltradoBase, tipoActivo, ordenSerie]);

  const tiposDisponibles = useMemo(() => {
    const set = new Set();
    inventario.forEach(p => set.add(p.tipo || 'OTROS'));
    return Array.from(set).sort();
  }, [inventario]);

  const getEstadoStock = (stock, stockMinimo) => {
    if (stock === 0) return { clase: 'sin-stock', texto: 'Sin Stock' };
    if (stock <= stockMinimo) return { clase: 'stock-bajo', texto: 'Stock Bajo' };
    return { clase: 'stock-normal', texto: 'Stock Normal' };
  };

  const productosStockBajo = inventario.filter(p => p.stock <= p.stockMinimo && p.stock > 0);
  const productosSinStock = inventario.filter(p => p.stock === 0);

  const abrirModalAlerta = (tipo) => {
    setTipoAlerta(tipo);
    setMostrarModalAlertas(true);
  };
  const cerrarModalAlerta = () => {
    setMostrarModalAlertas(false);
    setTipoAlerta(null);
  };

  // Cerrar desplegable al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tiposDropdownRef.current && !tiposDropdownRef.current.contains(event.target)) {
        setMostrarTipos(false);
      }
    };
    if (mostrarTipos) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mostrarTipos]);

  return (
    <div className="inventario-container">
      <h2>Gestión de Inventario</h2>
      {cargando && <p>Cargando productos...</p>}
      {error && <p style={{color:'red'}}>Error: {error}</p>}
      {(usuario.rol === 'admin' || usuario.rol === 'bodega') && (
        <button onClick={() => setMostrandoAgregar(true)} className="btn-confirmar">
          + Agregar Producto / Imprimir Etiquetas
        </button>
      )}

      {/* Búsqueda y organización */}
      <div className="busqueda-inventario" style={{ margin: '15px 0', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', width: '100%' }}>
        <input
          type="text"
          placeholder="Buscar producto (nombre o serie)"
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          style={{ padding: '8px 10px', minWidth: '260px' }}
        />
        <div style={{display:'flex',gap:6,alignItems:'center'}}>
          <span style={{fontSize:12,opacity:.7}}>Orden:</span>
          <button className={`btn-confirmar ${ordenSerie==='asc'?'activo':''}`} onClick={()=> setOrdenSerie('asc')}>Serie ↑</button>
          <button className={`btn-confirmar ${ordenSerie==='desc'?'activo':''}`} onClick={()=> setOrdenSerie('desc')}>Serie ↓</button>
          {ordenSerie && (
            <button className="btn-cancelar" onClick={()=> setOrdenSerie(null)}>Quitar orden</button>
          )}
        </div>
        <span style={{ fontSize: '0.9rem', color: '#555' }}>
          Mostrados: {inventarioFiltrado.length} / Cargados: {inventario.length}
        </span>
        {busqueda && (
          <button
            onClick={() => setBusqueda('')}
            className="btn-cancelar"
            style={{ padding: '6px 12px' }}
          >Limpiar</button>
        )}
        {(tipoActivo!=='ALL' || ordenSerie) && (
          <button
            onClick={() => { setTipoActivo('ALL'); setOrdenSerie(null); }}
            className="btn-cancelar"
            style={{ padding: '6px 12px' }}
          >Quitar filtros</button>
        )}
      </div>

      {/* Filtros por tipo - Botón desplegable */}
      <div ref={tiposDropdownRef} className="tipos-dropdown">
        <button 
          className="btn-modern primary" 
          onClick={() => setMostrarTipos(!mostrarTipos)}
          style={{display:'flex', alignItems:'center', gap:8, justifyContent:'space-between', minWidth:180}}
        >
          <span>Tipos: {tipoActivo === 'ALL' ? 'Todos' : tipoActivo}</span>
          <span style={{fontSize:10}}>{mostrarTipos ? '▲' : '▼'}</span>
        </button>
        {mostrarTipos && (
          <div className="tipos-dropdown-menu">
            <button 
              className={`btn-modern ${tipoActivo==='ALL'?'primary':'outline'}`}
              onClick={() => { setTipoActivo('ALL'); setMostrarTipos(false); }}
            >
              Todos
            </button>
            {tiposDisponibles.map(t => (
              <button 
                key={t} 
                className={`btn-modern ${tipoActivo===t?'primary':'outline'}`}
                onClick={() => { setTipoActivo(t); setMostrarTipos(false); }}
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* NUEVO: Botones de alertas de stock */}
      <div className="alert-buttons-wrapper">
        <button
          className={`alert-btn sin-stock-btn ${productosSinStock.length === 0 ? 'disabled' : ''}`}
          disabled={productosSinStock.length === 0}
          onClick={() => abrirModalAlerta('sin')}
        >
          🚫 Sin Stock ({productosSinStock.length})
        </button>
        <button
          className={`alert-btn bajo-stock-btn ${productosStockBajo.length === 0 ? 'disabled' : ''}`}
          disabled={productosStockBajo.length === 0}
          onClick={() => abrirModalAlerta('bajo')}
        >
          ⚠️ Stock Bajo ({productosStockBajo.length})
        </button>
      </div>

      {/* Modal Agregar Productos */}
      {mostrandoAgregar && (
        <div className="formulario-overlay">
          <div className="formulario-modal">
            <h3>Agregar Productos</h3>
            <div className="formulario-grid">
              <input placeholder="Nombre *" value={nuevoProducto.nombre} onChange={e => manejarCambioFormulario('nombre', e.target.value)} />
              <input placeholder="Serie *" value={nuevoProducto.serie} onChange={e => manejarCambioFormulario('serie', e.target.value)} />
              <input type="number" placeholder="Precio *" value={nuevoProducto.precio} onChange={e => manejarCambioFormulario('precio', e.target.value)} />
              <input type="number" placeholder="Stock" value={nuevoProducto.stock} onChange={e => manejarCambioFormulario('stock', e.target.value)} />
              <input type="number" placeholder="Stock mínimo" value={nuevoProducto.stockMinimo} onChange={e => manejarCambioFormulario('stockMinimo', e.target.value)} />
              {/* Campo de ubicación eliminado */}
              <input type="url" placeholder="URL Imagen" value={nuevoProducto.imagen} onChange={e => manejarCambioFormulario('imagen', e.target.value)} />
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <label style={{fontSize:12}}>Color POS</label>
                <input type="color" value={nuevoProducto.color || '#ffffff'} onChange={e => manejarCambioFormulario('color', e.target.value)} />
              </div>
            </div>
            <div className="formulario-botones">
              <button onClick={agregarProducto} className="btn-confirmar">Agregar Producto</button>
              <button onClick={() => setMostrandoImpresion(true)} className="btn-confirmar">🖨️ Imprimir Etiquetas</button>
              <button onClick={() => setMostrandoAgregar(false)} className="btn-cancelar">Cerrar</button>
              <button onClick={exportarEtiquetasExcel} className="btn-confirmar">📥 Exportar a Excel</button>
            </div>
            <h4 style={{ marginTop: '20px' }}>Productos Agregados</h4>
            <ul className="lista-productos">
              {inventario.map(p => (
                <li key={p.id}>{p.nombre} - {p.serie} (Stock: {p.stock})</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Modal Imprimir Etiquetas */}
      {mostrandoImpresion && (
        <div className="formulario-overlay">
          <div className="formulario-modal">
            <h3>Imprimir Etiquetas</h3>
            <input type="text" placeholder="Buscar producto" value={busquedaEtiquetas} onChange={e => setBusquedaEtiquetas(e.target.value)} />
            <h4>Selecciona dispositivo de impresión:</h4>
            <select value={dispositivoImpresion} onChange={e => setDispositivoImpresion(e.target.value)}>
              <option value="normal">Impresora Normal (OD5160)</option>
              <option value="niimbot">Niimbot B1 (rollo)</option>
            </select>
            <h4>Selecciona tamaño de etiqueta (solo para impresora normal):</h4>
            <select value={tamañoEtiqueta} onChange={e => setTamañoEtiqueta(e.target.value)} disabled={dispositivoImpresion === 'niimbot'}>
              <option value="minis">Minis</option>
              <option value="pequeño">Pequeño</option>
              <option value="mediano">Mediano</option>
              <option value="grande">Grande</option>
            </select>
            {(() => {
              const b = (busquedaEtiquetas||'').toLowerCase();
              const listaModal = inventario.filter(p => !b || (p.nombre||'').toLowerCase().includes(b) || (p.serie||'').toLowerCase().includes(b) || (p.talla||'').toLowerCase().includes(b));
              return (
            <ul className="lista-productos">
              {listaModal.map(p => (
                <li key={p.id}>
                  <input type="checkbox" checked={p.seleccionado || false} onChange={e => manejarSeleccionEtiqueta(p.id, e.target.checked)} />
                  {p.nombre} - {p.serie}{p.talla ? ` - ${p.talla}` : ''}
                  <input type="number" min="1" value={p.cantidadEtiquetas} style={{ width: '50px', marginLeft: '10px' }} onChange={e => manejarCantidadEtiqueta(p.id, e.target.value)} />
                </li>
              ))}
            </ul>
              );
            })()}
            <div className="formulario-botones">
              <button onClick={imprimirEtiquetas} className="btn-confirmar">🖨️ Imprimir</button>
              <button onClick={exportarEtiquetasExcel} className="btn-confirmar">📥 Exportar a Excel</button>
              <button onClick={() => setMostrandoImpresion(false)} className="btn-cancelar">Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* NUEVO: Modal de Alertas */}
      {mostrarModalAlertas && (
        <div className="formulario-overlay">
          <div className="formulario-modal modal-alertas">
            <h3>{tipoAlerta === 'sin' ? 'Productos Sin Stock' : 'Productos con Stock Bajo'}</h3>
            <table className="tabla-alertas">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Serie</th>
                  <th>Stock</th>
                  <th>Stock Mínimo</th>
                  <th>Acción Rápida</th>
                </tr>
              </thead>
              <tbody>
                {(tipoAlerta === 'sin' ? productosSinStock : productosStockBajo).map(p => (
                  <tr key={p.id}>
                    <td>{p.nombre}</td>
                    <td>{p.serie}</td>
                    <td className={p.stock === 0 ? 'sin-stock' : (p.stock <= p.stockMinimo ? 'stock-bajo' : '')}>{p.stock}</td>
                    <td>{p.stockMinimo}</td>
                    <td>
                      {(usuario.rol === 'admin' || usuario.rol === 'bodega') && (
                        <button className="btn-editar" onClick={() => { iniciarEdicion(p); cerrarModalAlerta(); }}>
                          Editar Stock
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="formulario-botones">
              <button className="btn-cancelar" onClick={cerrarModalAlerta}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Tabla Inventario */}
      <div className="inventario-tabla">
        <table>
          <thead>
            <tr>
              <th>Imagen</th><th>Producto</th><th>Serie</th><th>Precio</th>
              <th>Stock Actual</th><th>Stock Mínimo</th><th>Estado</th><th>Talla</th><th>Color</th><th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {inventarioFiltrado.map(producto => {
              const estadoStock = getEstadoStock(producto.stock, producto.stockMinimo);
              return (
                <tr key={producto.id} className={estadoStock.clase}>
                  <td data-label="Imagen"><img src={producto.imagen} alt={producto.nombre} width={50} /></td>
                  <td data-label="Producto">{producto.nombre}</td>
                  <td data-label="Serie">{producto.serie}</td>
                  <td data-label="Precio">
                    {productoEditando === producto.id ? (
                      <div className="stock-editable" style={{display:'flex',alignItems:'center',gap:6}}>
                        <input type="number" step="0.01" value={nuevoPrecio} onChange={e => setNuevoPrecio(e.target.value)} />
                        <button onClick={() => actualizarPrecio(producto.id, nuevoPrecio)}>✓</button>
                        <button onClick={cancelarEdicion}>✗</button>
                      </div>
                    ) : (
                      `$${Number(producto.precio).toFixed(2)}`
                    )}
                  </td>
                  <td data-label="Stock Actual">
                    {productoEditando === producto.id ? (
                      <div className="stock-editable">
                        <input type="number" value={nuevoStock} onChange={(e) => setNuevoStock(e.target.value)} />
                        <button onClick={() => actualizarStock(producto.id, nuevoStock)}>✓</button>
                        <button onClick={cancelarEdicion}>✗</button>
                      </div>
                    ) : (
                      <span onClick={() => iniciarEdicion(producto)} className="stock-editable">{producto.stock}</span>
                    )}
                  </td>
                  <td data-label="Stock Mínimo">{producto.stockMinimo}</td>
                  <td data-label="Estado"><span className={`estado-badge ${estadoStock.clase}`}>{estadoStock.texto}</span></td>
                  <td data-label="Talla">{producto.talla || '-'}</td>
                  <td data-label="Color">
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <div style={{width:18,height:18,borderRadius:4,background:getProductColor(producto.id) || producto.color || '#ffffff',border:'1px solid #ccc'}} />
                      <button className="btn-editar" onClick={() => {
                        // abrir selector de color nativo
                        const input = document.createElement('input');
                        input.type = 'color';
                        input.value = getProductColor(producto.id) || producto.color || '#ffffff';
                        input.onchange = (ev) => {
                          const v = ev.target.value;
                          setProductColor(producto.id, v);
                          setInventario(prev => prev.map(p => p.id === producto.id ? { ...p, color: v } : p));
                        };
                        input.click();
                      }}>Color</button>
                    </div>
                  </td>
                  <td data-label="Acciones">
                    {(usuario.rol === 'admin' || usuario.rol === 'bodega') ? (
                      <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
                        <button onClick={() => iniciarEdicion(producto)} className="btn-editar">Editar</button>
                        <button onClick={() => abrirModalImagenes(producto)} className="btn-confirmar" style={{fontSize:'11px'}}>Imágenes</button>
                        <button onClick={() => abrirModalVariantes(producto)} className="btn-confirmar" style={{fontSize:'11px',background:'#9b59b6'}}>Variantes</button>
                        <button onClick={() => eliminarProducto(producto.id, producto.nombre)} className="btn-cancelar" style={{fontSize:'11px'}}>Eliminar</button>
                      </div>
                    ) : "Sin permisos"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal de Gestión de Imágenes */}
      {modalImagenes.open && (
        <div className="formulario-overlay">
          <div className="formulario-modal" style={{maxWidth: '800px'}}>
            <h3>Gestión de Imágenes - {modalImagenes.producto?.nombre}</h3>
            
            <div style={{marginBottom: '15px'}}>
              <label style={{display: 'block', marginBottom: '8px', fontWeight: 'bold'}}>
                Subir Imágenes (máx. 10)
              </label>
              <input 
                type="file" 
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                multiple
                onChange={subirImagenes}
                disabled={subiendoImagenes}
                style={{padding: '8px'}}
              />
              {subiendoImagenes && <p style={{fontSize: '12px', color: '#666'}}>Subiendo imágenes...</p>}
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: '15px',
              marginTop: '20px',
              maxHeight: '400px',
              overflowY: 'auto',
              padding: '10px'
            }}>
              {imagenesProducto.length === 0 ? (
                <p style={{fontSize: '12px', color: '#666', gridColumn: '1 / -1'}}>
                  No hay imágenes. Sube algunas usando el botón de arriba.
                </p>
              ) : (
                imagenesProducto.map(img => (
                  <div key={img.id_imagen} style={{
                    border: img.es_principal ? '3px solid #4CAF50' : '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '8px',
                    position: 'relative',
                    background: '#fff'
                  }}>
                    <img 
                      src={img.imagen_url} 
                      alt="Producto" 
                      style={{
                        width: '100%',
                        height: '120px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        marginBottom: '8px'
                      }}
                    />
                    {img.es_principal && (
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        background: '#4CAF50',
                        color: '#fff',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 'bold'
                      }}>
                        PRINCIPAL
                      </div>
                    )}
                    <div style={{display: 'flex', gap: '4px', flexDirection: 'column'}}>
                      {!img.es_principal && (
                        <button 
                          onClick={() => establecerImagenPrincipal(img.id_imagen)}
                          className="btn-confirmar"
                          style={{fontSize: '10px', padding: '4px 6px', width: '100%'}}
                        >
                          Hacer Principal
                        </button>
                      )}
                      <button 
                        onClick={() => eliminarImagen(img.id_imagen)}
                        className="btn-cancelar"
                        style={{fontSize: '10px', padding: '4px 6px', width: '100%'}}
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="formulario-botones" style={{marginTop: '20px'}}>
              <button onClick={cerrarModalImagenes} className="btn-cancelar">Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Gestión de Variantes */}
      {modalVariantes.open && (
        <div className="formulario-overlay">
          <div className="formulario-modal" style={{maxWidth: '900px'}}>
            <h3>Gestión de Variantes - {modalVariantes.producto?.nombre}</h3>
            
            <div style={{marginBottom: '20px', padding: '15px', background: '#f9f9f9', borderRadius: '8px'}}>
              <h4 style={{marginTop: 0, marginBottom: 15}}>Agregar Color</h4>
              <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <label style={{fontSize:14,fontWeight:600}}>Color:</label>
                  <input 
                    type="color" 
                    value={nuevaVariante.color} 
                    onChange={e => setNuevaVariante(prev => ({ ...prev, color: e.target.value }))}
                    style={{width:60,height:40,cursor:'pointer',border:'2px solid #ddd',borderRadius:4}}
                  />
                </div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <label style={{fontSize:14,fontWeight:600}}>Stock:</label>
                  <input 
                    type="number" 
                    placeholder="0" 
                    value={nuevaVariante.stock} 
                    onChange={e => setNuevaVariante(prev => ({ ...prev, stock: e.target.value }))}
                    style={{padding: '8px',width:80}}
                  />
                </div>
                <button onClick={crearVariante} className="btn-confirmar" style={{marginLeft:10}}>
                  + Agregar
                </button>
              </div>
            </div>

            <div style={{maxHeight: '400px', overflowY: 'auto'}}>
              {variantes.length === 0 ? (
                <p style={{textAlign: 'center', color: '#666'}}>No hay variantes. Crea una usando el formulario arriba.</p>
              ) : (
                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                  <thead>
                    <tr style={{background: '#f0f0f0'}}>
                      <th style={{padding: '12px', textAlign: 'left'}}>Color</th>
                      <th style={{padding: '12px', textAlign: 'center'}}>Stock</th>
                      <th style={{padding: '12px', textAlign: 'center'}}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variantes.map(v => (
                      <tr key={v.id_variante} style={{borderBottom: '1px solid #eee'}}>
                        <td style={{padding: '12px'}}>
                          <div style={{display:'flex',alignItems:'center',gap:10}}>
                            <div style={{width:40,height:40,borderRadius:6,background:v.color,border:'2px solid #ddd'}} />
                            <span style={{fontSize:14,fontWeight:500}}>{v.color}</span>
                          </div>
                        </td>
                        <td style={{padding: '12px', textAlign: 'center'}}>
                          {editandoVariante === v.id_variante ? (
                            <input 
                              type="number" 
                              defaultValue={v.stock} 
                              onBlur={e => { actualizarVariante(v.id_variante, { stock: parseInt(e.target.value) || 0 }); setEditandoVariante(null); }}
                              autoFocus
                              style={{width:80,padding:6,textAlign:'center',fontSize:14}}
                            />
                          ) : (
                            <span style={{fontSize:16,fontWeight:600,cursor:'pointer'}} onClick={() => setEditandoVariante(v.id_variante)}>
                              {v.stock}
                            </span>
                          )}
                        </td>
                        <td style={{padding: '12px', textAlign: 'center'}}>
                          <button 
                            onClick={() => eliminarVariante(v.id_variante)}
                            className="btn-cancelar"
                            style={{fontSize:11,padding:'6px 12px'}}
                          >
                            Eliminar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="formulario-botones" style={{marginTop: '20px'}}>
              <button onClick={cerrarModalVariantes} className="btn-cancelar">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Inventario;